import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import {
  purchaseMatchesShowPricesByGroupAllowedRoutes,
  resolveShowPricesByGroupConfig,
} from 'utils/showPricesByGroup';

/**
 * Encapsulates the "show prices by group" behaviour for the seat selection flow only.
 * Requires: USE_SEAT_BASED_PRICING (whitelabel) + show_prices_by_group (GrowthBook), and showPricesByGroupConfig:
 * - Config missing: never shown.
 * - enabledOnAllRoutes === true: shown on all routes.
 * - enabledOnAllRoutes === false: shown only if the route is in showPricesByGroupConfig.allowedRoutes.
 *
 * Pure route logic lives in {@link utils/showPricesByGroup}.
 *
 * @returns {boolean} True when flags and route config allow showing the feature.
 */
const useShowPricesByGroup = () => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { showPricesByGroupConfig } = useWhitelabelEnvs();
  // `state.purchase.toJS()` in the selector rebuilds the whole purchase on every
  // dispatch and returns a new reference each time, so this hook re-rendered on
  // unrelated state (RR-5/SM-2). Only the two legs are read, and that is also the
  // documented shape of `purchaseMatchesShowPricesByGroupAllowedRoutes`.
  const departsState = useSelector((state) => state.purchase?.get?.('departs'));
  const returnsState = useSelector((state) => state.purchase?.get?.('returns'));
  const purchase = useMemo(
    () => ({ departs: departsState?.toJS(), returns: returnsState?.toJS() }),
    [departsState, returnsState],
  );
  const search = useSelector((state) => state.search);
  const showPricesByGroupGB = useGrowthBookFeatureValue('show_prices_by_group');

  const flagsOn = Boolean(features.USE_SEAT_BASED_PRICING) && Boolean(showPricesByGroupGB);
  if (!flagsOn) return false;

  const resolved = resolveShowPricesByGroupConfig(showPricesByGroupConfig);
  if (resolved === false) return false;
  if (resolved === true) return true;

  return purchaseMatchesShowPricesByGroupAllowedRoutes(purchase, search, resolved);
};

export default useShowPricesByGroup;
